// src/pages/AttendanceOverview/components/ShiftManagementTab.js
import React, { useEffect, useState } from "react";
import {
  Card,
  Table,
  Button,
  Space,
  Tooltip,
  Row,
  Col,
  App,
  message,
  Popconfirm,
  Drawer,
  Flex,
  Grid
} from "antd";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchShiftReport,
  fetchAllEmployees,
  addNewShift,
  deleteShift,
} from "../attendanceOverviewThunk";
import {
  selectShiftReport,
  selectEmployees,
  selectAttendanceCalendar,
} from "../attendanceOverviewSelectors";
import dayjs from "dayjs";
import { accessChecker } from "../../../lib/functions";
import { EditOutlined, DeleteOutlined, PlusOutlined, QuestionCircleOutlined } from "@ant-design/icons";
import RecordDateFilter from "../../../components/filters/RecordDateFilter";
import ShiftManagementModal from "./modals/ShiftManagementModal";
import CommonDateRangeFilter from "../../../components/common/filters/CommonDateRangeFilter";

const ShiftManagementTab = () => {
  const dispatch = useDispatch();
  const shiftReport = useSelector(selectShiftReport) || [];
  const employees = useSelector(selectEmployees) || [];
  const [open, setOpen] = useState(false);
  const calendar = useSelector(selectAttendanceCalendar);
  const [disabled, setDisabled] = useState(false);
  const authInfo = useSelector((state) => state.auth.auth_info);
  const currentEmployeeId = authInfo?.employee_id || null;
  const [editingShift, setEditingShift] = useState(null);
  const [helpOpen, setHelpOpen] = useState(false);
  const { modal } = App.useApp();
  const { useBreakpoint } = Grid;
  const screens = useBreakpoint();
  const [filters, setFilters] = useState({
    fromDate: dayjs().startOf("month").format("YYYY-MM-DD"),
    toDate: dayjs().endOf("month").format("YYYY-MM-DD")
  });
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });

  useEffect(() => {
    dispatch(fetchShiftReport(filters));
  }, [dispatch, filters]);

  const columns = [
    { title: "No.", render: (_, __, i) => i + 1, },
    { title: "Shift Time", dataIndex: "shift_time", key: "shift_time" },
    {
      title: "Start Date",
      dataIndex: "shift_start_date",
      key: "shift_start_date",
      render: (v) => (v ? dayjs(v).format("YYYY-MMM-DD") : "-"),
    },
    {
      title: "End Date",
      dataIndex: "shift_end_date",
      key: "shift_end_date",
      render: (v) => (v ? dayjs(v).format("YYYY-MMM-DD") : "-"),
    },
    { title: "Total Staffs", dataIndex: "user_count", key: "user_count" },
    { title: "Notes", dataIndex: "notes", key: "notes" },
    {
      title: "Users",
      dataIndex: "users",
      key: "users",
      render: (value) => (
        <Tooltip placement="topLeft" title={value} mouseEnterDelay={0.3}>
          <span style={{ color: "#0265DC", fontWeight: 500, fontSize: "12px" }}>
            {value}
          </span>
        </Tooltip>
      ),
    },
    accessChecker("approve_attendance") && {
      title: "Actions",
      key: "actions",
      align: "right",
      render: (_, record, index) => (
        <Space>
          <Tooltip title="Edit">
            <Button
              type="text"
              icon={<EditOutlined style={{ color: "#1677ff" }} />}
              onClick={() => handleEdit(record)}
            />
          </Tooltip>

          <Popconfirm
            title="Are you sure to delete this Shift?"
            okText="Yes"
            cancelText="No"
            onConfirm={() => handleDelete(record)}
          >
            <Tooltip title="Delete">
              <Button
                type="text"
                icon={<DeleteOutlined style={{ color: "red" }} />}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const handleDelete = async (values) => {
    try {
      let employeeIds = [];

      if (Array.isArray(values.emp_ids)) {
        // If already an array
        employeeIds = values.emp_ids.map(Number);
      } else if (typeof values.emp_ids === "string") {
        // If it's a comma-separated string
        employeeIds = values.emp_ids
          .split(",")
          .map((id) => id.trim())
          .filter(Boolean); // remove NaN/empty
      }
      const payload = {
        employee_id: employeeIds,
        enddate: values.shift_start_date,
        startdate: values.shift_end_date,
      };

      await dispatch(deleteShift(payload)).unwrap();
      message.success("Shift deleted successfully!");
      dispatch(fetchShiftReport(filters));
    } catch (err) {
      message.error(err.message || "Failed to delete the Shift");
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  /** ✏️ Edit Handler */
  const handleEdit = (record) => {
    // Compute HR membership (same role list as old code)
    const hrRoles = [
      "ForthERP_Basic_hr",
      "ForthERP_Standard_hr",
      "ForthERP_Pro_hr",
      "PharmaERP_Basic_hr",
      "PharmaERP_Standard_hr",
      "PharmaERP_Pro_hr",
      "PharmaBooks_Basic_hr",
      "PharmaBooks_Standard_hr",
      "PharmaBooks_Pro_hr",
      "ForthBooks_Basic_hr",
      "ForthBooks_Standard_hr",
      "ForthBooks_Pro_hr",
      "HRPayroll_Standard_hr",
      "HRPayroll_Pro_hr",
      "SuperERP_hr",
      'ForthCRM_Pro_hr',
      'ForthCRM_Standard_hr',
      // admin set (if admins should behave like HR in old app)
      "ForthERP_Basic_admin",
      "ForthERP_Standard_admin",
      "ForthERP_Pro_admin",
      "PharmaERP_Basic_admin",
      "PharmaERP_Standard_admin",
      "PharmaERP_Pro_admin",
      "PharmaBooks_Basic_admin",
      "PharmaBooks_Standard_admin",
      "PharmaBooks_Pro_admin",
      "ForthBooks_Basic_admin",
      "ForthBooks_Standard_admin",
      "ForthBooks_Pro_admin",
      "HRPayroll_Standard_admin",
      "HRPayroll_Pro_admin",
      "SuperERP_admin",
      "ForthCRM_Standard_admin",
      "ForthCRM_Pro_admin",
    ];

    const userRoles = authInfo?.userInfo?.user_role_names
      ? authInfo.userInfo.user_role_names.split(",").map((r) => r.trim())
      : [];

    const isHR = userRoles.some((role) => hrRoles.includes(role));

    // parse shift_time (expected "HH:mm - HH:mm" or similar)
    const [startTime, endTime] = (record.shift_time || "").split(" - ");
    const today = dayjs().format("YYYY-MM-DD");

    const parsedInTime = startTime ? dayjs(`${today} ${startTime}`) : dayjs();
    const parsedOutTime = endTime ? dayjs(`${today} ${endTime}`) : dayjs();

    // user_ids may be string "1,2,3" or array; normalize to array of numbers
    let userIds = [];
    if (Array.isArray(record.user_ids)) {
      userIds = record.user_ids.map((v) => v);
    } else if (typeof record.user_ids === "string") {
      userIds = record.user_ids
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
    } else if (record.user_ids) {
      userIds = [record.user_ids];
    }

    // If not HR, remove current employee id from selection (old behavior)
    let defaultSelected = userIds;
    if (!isHR && currentEmployeeId) {
      defaultSelected = userIds.filter(
        (id) => id !== currentEmployeeId
      );
    }

    // attach editingShift so modal can prefill from prop
    const editing = {
      ...record,
      _prefill: {
        from_date: record.shift_start_date
          ? dayjs(record.shift_start_date)
          : null,
        to_date: record.shift_end_date ? dayjs(record.shift_end_date) : null,
        check_in: parsedInTime.isValid() ? parsedInTime : dayjs(),
        check_out: parsedOutTime.isValid() ? parsedOutTime : dayjs(),
        notes: record.notes || "",
        user_ids: defaultSelected,
      },
    };

    setEditingShift(editing);
    setOpen(true);
    dispatch(fetchAllEmployees());
    // ensure employees list available
  };

  // 🧠 handleAddNewShift — triggered from ShiftManagementModal
  const handleAddNewShift = async ({
    from_date,
    to_date,
    check_in,
    check_out,
    user_ids,
    notes,
  }) => {
    try {
      setDisabled(true);

      // ✅ payload structure
      const payload = {
        employee_id: user_ids,
        startDate: from_date,
        endDate: to_date,
        check_in: check_in,
        check_out: check_out,
        notes,
      };

      // 🚀 Replace this with your API or Redux action

      // Example if you later add thunk:
      await dispatch(addNewShift(payload)).unwrap();

      modal.success({ title: "Shift Added Successfully" });

      // Refresh the list after adding
      await dispatch(fetchShiftReport(filters)).unwrap();
      setEditingShift(null);
      setOpen(false);
    } catch (err) {
      modal.error({
        title: "Failed to Add Shift",
        content: err.message || "Unexpected error occurred",
      });
    } finally {
      setDisabled(false);
    }
  };

  const openModal = async () => {
    setOpen(true);
    dispatch(fetchAllEmployees());
  };

  const onModalClose = () => {
    setOpen(false);
    setEditingShift(null);
  };

  return (
    <Card>
      <div style={{ marginBottom: 12 }}>
        <Row
          gutter={[12, 12]}
          align="bottom"
          wrap
          style={{ marginBottom: 12 }}
        >
          <CommonDateRangeFilter
            filters={filters}
            onChange={(fromDate, toDate) => {
              handleFilterChange("fromDate", fromDate);
              handleFilterChange("toDate", toDate);
            }}
          />

          <Col>
            <div style={{ height: 58, display: "flex", alignItems: "flex-end" }}>
              <Button
                onClick={openModal}
                type="primary"
                icon={<PlusOutlined />}
              >
                Add New Shift
              </Button>
            </div>
          </Col>

          <Col>
            <div style={{ height: 58, display: "flex", alignItems: "flex-end" }}>
              <Tooltip title="Help">
                <Button
                  type="text"
                  icon={<QuestionCircleOutlined />}
                  onClick={() => setHelpOpen(true)}
                />
              </Tooltip>
            </div>
          </Col>
        </Row>
        <style>{`
        @media (min-width: 1200px) and (max-width: 1560px) {
        .page-filters .ant-row .ant-col {
          flex: 0 0 23.33% !important;
          max-width: 23.33% !important;
        }
      }

                  @media (min-width: 768px) and (max-width: 1200px){
                    .page-filters .ant-row .ant-col {
                      flex: 0 0 50% !important;
                      max-width: 50% !important;
                    }
                  }
                  @media (min-width: 768px) {
        :where(.css-dev-only-do-not-override-1j5i8gz).ant-col-md-4 {
          max-width: none !important;
          flex: none !important;
        }
        `}</style>
      </div>

      <Table
        columns={columns.filter(Boolean)}
        dataSource={shiftReport}
        rowKey={(r) => r.id || r.shift_id || Math.random()}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,

          showSizeChanger: true,
          showQuickJumper: true,

          pageSizeOptions: ["10", "20", "50", "100"],

          showTotal: (total, range) =>
            `${range[0]}-${range[1]} of ${total} items`,

          onChange: (page, pageSize) => {
            setPagination({
              current: page,
              pageSize,
            });
          },

          onShowSizeChange: (current, size) => {
            setPagination({
              current: 1,
              pageSize: size,
            });
          },
        }}
        bordered
        size={"middle"}
        scroll={{ x: "max-content" }}
      />
      <Drawer
        title="Shift Management Help"
        open={helpOpen}
        onClose={() => setHelpOpen(false)}
        width={460}
        styles={{
          wrapper: {
            paddingTop: !screens.md ? "80px" : undefined
          }
        }}
      >
        <div style={{ lineHeight: 1.8 }}>
          <h2
            style={{
              fontSize: 18,
              fontWeight: 600,
              marginBottom: 12,
              marginLeft: 12
            }}
          >
            Employee Shift Management
          </h2>

          <p style={{ color: "#555", marginBottom: 16, marginLeft: 17 }}>
            This section helps manage employee shift schedules,
            shift timings, work assignments, and attendance-related
            shift operations.
          </p>

          <div
            style={{
              background: "#f5f5f5",
              padding: 14,
              borderRadius: 8,
              marginBottom: 16,
            }}
          >
            <strong>Available Features</strong>

            <ol style={{ paddingLeft: 20, marginTop: 10 }}>
              <li>Create employee work shifts</li>
              <li>Assign shifts to multiple employees</li>
              <li>Manage shift start and end dates</li>
              <li>Configure check-in and check-out timings</li>
              <li>Edit existing shift schedules</li>
              <li>Delete outdated shift records</li>
              <li>Filter shifts using date ranges</li>
              <li>Track assigned employees for each shift</li>
              <li>Manage attendance-related shift workflows</li>
            </ol>
          </div>

          <div
            style={{
              background: "#fafafa",
              border: "1px solid #eee",
              padding: 14,
              borderRadius: 8,
            }}
          >
            <strong>Important Notes</strong>

            <ul
              style={{
                paddingLeft: 18,
                marginTop: 10,
                color: "#666",
              }}
            >
              <li>Shift schedules directly affect attendance workflows.</li>
              <li>Employees can be assigned to multiple shifts if required.</li>
              <li>Date filters help locate shift records quickly.</li>
              <li>Shift timings impact attendance calculations.</li>
              <li>Only authorized users can manage shift operations.</li>
            </ul>
          </div>
        </div>
      </Drawer>
      <ShiftManagementModal
        visible={open}
        onClose={onModalClose}
        onAddShift={handleAddNewShift}
        allUsers={employees}
        weekEndDays={calendar?.weekendDays?.[0]?.weekend_days || []}
        isDisabled={disabled}
        initialData={editingShift}
      />
    </Card>
  );
};

export default ShiftManagementTab;
